import React from 'react';
import { Heading2, AlignLeft, List, Quote, Trash2, ChevronUp, ChevronDown, Plus } from 'lucide-react';

export type BlockType = 'heading' | 'paragraph' | 'list' | 'quote';

export interface ContentBlock {
    id: string;
    type: BlockType;
    text: string;
    items: string[];
}

export function genId(): string {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function parseContentToBlocks(content: string): ContentBlock[] {
    if (!content || content.trim() === '') {
        return [{ id: genId(), type: 'paragraph', text: '', items: [] }];
    }
    if (content.trim().startsWith('[')) {
        try {
            return JSON.parse(content);
        } catch {
            return [{ id: genId(), type: 'paragraph', text: content, items: [] }];
        }
    }
    // Parse legacy HTML
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const blocks: ContentBlock[] = [];
        doc.body.childNodes.forEach(node => {
            if (node.nodeType !== 1) return;
            const el = node as HTMLElement;
            const text = el.textContent?.trim() || '';
            const tag = el.tagName.toLowerCase();
            if (!text) return;
            if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
                blocks.push({ id: genId(), type: 'heading', text, items: [] });
            } else if (tag === 'p') {
                blocks.push({ id: genId(), type: 'paragraph', text, items: [] });
            } else if (tag === 'ul' || tag === 'ol') {
                const items = Array.from(el.querySelectorAll('li')).map(li => li.textContent?.trim() || '').filter(Boolean);
                if (items.length) blocks.push({ id: genId(), type: 'list', text: '', items });
            } else if (tag === 'blockquote') {
                blocks.push({ id: genId(), type: 'quote', text, items: [] });
            }
        });
        return blocks.length > 0 ? blocks : [{ id: genId(), type: 'paragraph', text: content, items: [] }];
    } catch {
        return [{ id: genId(), type: 'paragraph', text: content, items: [] }];
    }
}

const BLOCK_META: Record<BlockType, { label: string; icon: React.ReactNode; color: string }> = {
    heading:   { label: 'Heading',   icon: <Heading2 className="w-4 h-4" />,  color: 'text-purple-600 bg-purple-50' },
    paragraph: { label: 'Paragraph', icon: <AlignLeft className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
    list:      { label: 'List',      icon: <List className="w-4 h-4" />,       color: 'text-green-600 bg-green-50' },
    quote:     { label: 'Quote',     icon: <Quote className="w-4 h-4" />,      color: 'text-amber-600 bg-amber-50' },
};

interface Props {
    blocks: ContentBlock[];
    onChange: (blocks: ContentBlock[]) => void;
}

const BlogBlockEditor: React.FC<Props> = ({ blocks, onChange }) => {

    const update = (id: string, patch: Partial<ContentBlock>) => {
        onChange(blocks.map(b => b.id === id ? { ...b, ...patch } : b));
    };

    const remove = (id: string) => {
        const next = blocks.filter(b => b.id !== id);
        onChange(next.length ? next : [{ id: genId(), type: 'paragraph', text: '', items: [] }]);
    };

    const move = (id: string, dir: -1 | 1) => {
        const idx = blocks.findIndex(b => b.id === id);
        const target = idx + dir;
        if (target < 0 || target >= blocks.length) return;
        const next = [...blocks];
        [next[idx], next[target]] = [next[target], next[idx]];
        onChange(next);
    };

    const add = (type: BlockType) => {
        onChange([...blocks, { id: genId(), type, text: '', items: type === 'list' ? [''] : [] }]);
    };

    const updateListItem = (blockId: string, itemIdx: number, value: string) => {
        const block = blocks.find(b => b.id === blockId)!;
        const items = [...block.items];
        items[itemIdx] = value;
        update(blockId, { items });
    };

    const addListItem = (blockId: string) => {
        const block = blocks.find(b => b.id === blockId)!;
        update(blockId, { items: [...block.items, ''] });
    };

    const removeListItem = (blockId: string, itemIdx: number) => {
        const block = blocks.find(b => b.id === blockId)!;
        const items = block.items.filter((_, i) => i !== itemIdx);
        update(blockId, { items: items.length ? items : [''] });
    };

    return (
        <div className="space-y-3">
            {blocks.map((block, idx) => {
                const meta = BLOCK_META[block.type];
                return (
                    <div key={block.id} className="flex gap-3 items-start group">

                        {/* Block type badge */}
                        <div className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold mt-1 ${meta.color}`}>
                            {meta.icon}
                            <span className="hidden sm:inline">{meta.label}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            {block.type === 'heading' && (
                                <input
                                    type="text"
                                    value={block.text}
                                    onChange={e => update(block.id, { text: e.target.value })}
                                    className="w-full font-serif font-bold text-xl text-gray-800 bg-gray-50 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                    placeholder="Section title…"
                                />
                            )}
                            {block.type === 'paragraph' && (
                                <textarea
                                    rows={3}
                                    value={block.text}
                                    onChange={e => update(block.id, { text: e.target.value })}
                                    className="w-full text-gray-700 bg-gray-50 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm leading-relaxed resize-y"
                                    placeholder="Write your paragraph here…"
                                />
                            )}
                            {block.type === 'quote' && (
                                <textarea
                                    rows={2}
                                    value={block.text}
                                    onChange={e => update(block.id, { text: e.target.value })}
                                    className="w-full italic text-gray-600 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm leading-relaxed resize-y"
                                    placeholder="Quote or highlight…"
                                />
                            )}
                            {block.type === 'list' && (
                                <div className="space-y-2">
                                    {block.items.map((item, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="text-gray-400 font-bold text-sm w-4 shrink-0">•</span>
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={e => updateListItem(block.id, i, e.target.value)}
                                                className="flex-1 text-gray-700 bg-gray-50 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                                placeholder={`List item ${i + 1}…`}
                                            />
                                            <button
                                                onClick={() => removeListItem(block.id, i)}
                                                className="p-1.5 text-gray-300 hover:text-red-400 transition-colors shrink-0"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => addListItem(block.id)}
                                        className="flex items-center gap-1.5 text-xs font-bold text-green-600 hover:text-green-700 pl-6 transition-colors"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Add item
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Controls */}
                        <div className="flex flex-col gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => move(block.id, -1)} disabled={idx === 0} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-20">
                                <ChevronUp className="w-4 h-4" />
                            </button>
                            <button onClick={() => move(block.id, 1)} disabled={idx === blocks.length - 1} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-20">
                                <ChevronDown className="w-4 h-4" />
                            </button>
                            <button onClick={() => remove(block.id)} className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                );
            })}

            {/* Add block buttons */}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-dashed border-gray-200">
                {(Object.keys(BLOCK_META) as BlockType[]).map(type => (
                    <button
                        key={type}
                        onClick={() => add(type)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${BLOCK_META[type].color} border-transparent hover:border-current`}
                    >
                        {BLOCK_META[type].icon}
                        + {BLOCK_META[type].label}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default BlogBlockEditor;
